"use client"

import { useState, useEffect, useCallback } from "react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  ArrowLeft,
  ArrowRight,
  BedDouble,
  ChefHat,
  Sofa,
  Bath,
  Briefcase,
  Utensils,
  Check,
  BedSingle,
  Flower as Shower,
  Baby,
  Gamepad2,
  Users,
  DoorOpen,
  Coffee,
  Wine,
  UtensilsCrossed,
  Shirt,
  Heart,
  Armchair,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react"
import { WizardStepper } from "./WizardStepper"
import { RoomSizeSelector } from "./RoomSizeSelector"
import { StyleSelector } from "./StyleSelector"
import RoomColorSelections from "@/app/components/RoomColorSelections"
import { GeneratedDesigns } from "./GeneratedDesigns"
import GenerationLoadingComponent from "@/app/components/GenerationLoadingComponent"
import { startScratchConceptWizard, appendGenerationImages, saveProject } from "@/src/utils/userDataApi"
import { useSession } from "@/app/context/AuthContext"
import { useBillableActions } from "@/app/context/BillableActionsContext"
import { generateRoomConcepts } from "@/src/utils/aiAggregatorApi"
import { BillingErrorComponent } from "@/app/components/BillingErrorComponent"
import { isBillingError } from "@/src/utils/billingApi"
import { ROOM_TYPE_MAPPINGS } from "@/src/utils/api-value-mappings"
import { useWizardStatePreservation, type BaseWizardState } from "@/hooks/useWizardStatePreservation"

interface StartFromScratchWizardState extends BaseWizardState {
  colorSubStep: number
  roomTypePage: number
  selections: {
    roomType: string
    roomSize: string
    style: string
    colorDetails: string
  }
  colorPreferences: {
    preferHues: string[]
    avoidHues: string[]
  }
  selectedMood: string
  selectedIntensity: string
  selectedAccentWall: boolean
  sessionId: string | null
  wizardId: string | null
  uiSessionId: string | null
  generatedDesigns: string[]
  apiDesignImageUrls: string[]
}

const steps = ["Room Type", "Room Size", "Style", "Color Preferences", "Generated Designs"]

const roomTypes = ROOM_TYPE_MAPPINGS.sort((a, b) => a.order - b.order)

const iconMap = {
  Sofa,
  BedDouble,
  ChefHat,
  Bath,
  Briefcase,
  Utensils,
  BedSingle,
  Shower,
  Baby,
  Gamepad2,
  Users,
  DoorOpen,
  Coffee,
  Wine,
  UtensilsCrossed,
  Shirt,
  Heart,
  Armchair,
}

const ROOM_TYPES_PER_PAGE = 6

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" })
}

export function StartFromScratchWizard() {
  const { data: session, status, refreshCredits } = useSession()
  const { getActionCost } = useBillableActions()
  const [currentStep, setCurrentStep] = useState(0)
  const [colorSubStep, setColorSubStep] = useState(1)
  const [colorError, setColorError] = useState<string | null>(null)
  const [colorPreferences, setColorPreferences] = useState<{ preferHues: string[]; avoidHues: string[] }>({
    preferHues: [],
    avoidHues: [],
  })
  const [selections, setSelections] = useState({
    roomType: "",
    roomSize: "",
    style: "",
    colorDetails: "",
  })
  const [isGenerating, setIsGenerating] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [generatedDesigns, setGeneratedDesigns] = useState<string[]>([])
  const [selectedDesign, setSelectedDesign] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)

  const [roomTypePage, setRoomTypePage] = useState(0)

  // ID management for persistence
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [wizardId, setWizardId] = useState<string | null>(null)
  const [uiSessionId, setUiSessionId] = useState<string | null>(null)
  const [isCreatingProject, setIsCreatingProject] = useState(false)

  // API URL tracking state
  const [apiDesignImageUrls, setApiDesignImageUrls] = useState<string[]>([])

  const [selectedMood, setSelectedMood] = useState<string>("balanced") // Changed from "Balanced" to "balanced" to match button values
  const [selectedIntensity, setSelectedIntensity] = useState<string>("medium") // Changed from "Medium" to "medium" to match button values
  const [selectedAccentWall, setSelectedAccentWall] = useState<boolean>(false)

  const { isRestoring, hasRestoredState, onBeforePurchase, clearSavedState } =
    useWizardStatePreservation<StartFromScratchWizardState>({
      wizardId: "start-from-scratch",
      state: {
        wizardType: "start-from-scratch",
        timestamp: Date.now(),
        userId: session?.user?.id || "", // Capture user ID for validation
        currentStep,
        colorSubStep,
        roomTypePage,
        selections,
        colorPreferences,
        selectedMood,
        selectedIntensity,
        selectedAccentWall,
        sessionId,
        wizardId,
        uiSessionId,
        generatedDesigns,
        apiDesignImageUrls,
      },
      onRestore: (restored) => {
        console.log("[v0] Restoring StartFromScratch wizard state:", restored)

        // Restore all state from saved data
        setCurrentStep(restored.currentStep)
        setColorSubStep(restored.colorSubStep)
        setRoomTypePage(restored.roomTypePage)
        setSelections(restored.selections)
        setColorPreferences(restored.colorPreferences)
        setSelectedMood(restored.selectedMood)
        setSelectedIntensity(restored.selectedIntensity)
        setSelectedAccentWall(restored.selectedAccentWall)
        setSessionId(restored.sessionId)
        setWizardId(restored.wizardId)
        setUiSessionId(restored.uiSessionId)
        setGeneratedDesigns(restored.generatedDesigns)
        setApiDesignImageUrls(restored.apiDesignImageUrls)

        console.log("[v0] Successfully restored StartFromScratch wizard state")
      },
      validateState: (state) => {
        // Reject stale state (older than 1 hour)
        const isRecent = Date.now() - state.timestamp < 3600000
        if (!isRecent) {
          console.warn("[v0] Rejected stale StartFromScratch state")
          return false
        }

        // Validate user matches
        if (state.userId !== session?.user?.id) {
          console.warn("[v0] Rejected StartFromScratch state - user mismatch")
          return false
        }

        return true
      },
    })

  useEffect(() => {
    scrollToTop()
  }, [currentStep])

  useEffect(() => {
    // Clear session IDs and generated designs if selections change after generation
    if ((sessionId || wizardId || uiSessionId) && currentStep < 4) {
      setSessionId(null)
      setWizardId(null)
      setUiSessionId(null)
      setGeneratedDesigns([])
      setSelectedDesign(null)
      setApiDesignImageUrls([])
    }
  }, [selections.roomType, selections.roomSize, selections.style, selections.colorDetails])

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Only suppress errors from our AI API that we're already handling
      if (event.reason?.message?.includes("An error occurred processing your request")) {
        console.log("[v0] Suppressed unhandled promise rejection:", event.reason.message)
        event.preventDefault()
      }
    }

    window.addEventListener("unhandledrejection", handleUnhandledRejection)

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection)
    }
  }, [])

  const updateSelection = (key: string, value: string) => {
    setSelections((prev) => ({ ...prev, [key]: value }))
    if (key === "colorDetails" && value) {
      setColorError(null)
    }
  }

  const handleColorPreferencesChange = (
    colorDetails: string,
    preferences?: { preferHues: string[]; avoidHues: string[] },
  ) => {
    updateSelection("colorDetails", colorDetails)
    if (preferences) {
      setColorPreferences(preferences)
    }
  }

  const handleColorDetailsChange = useCallback(
    (
      colorDetails: string,
      preferHues: string[],
      avoidHues: string[],
      mood: string,
      intensity: string,
      accentWall: boolean,
    ) => {
      console.log("[v0] Color details changed:", { mood, intensity, accentWall, preferHues, avoidHues })
      updateSelection("colorDetails", colorDetails)
      setColorPreferences({ preferHues, avoidHues })
      setSelectedMood(mood)
      setSelectedIntensity(intensity)
      setSelectedAccentWall(accentWall)
      setColorError(null)
    },
    [],
  )

  const nextStep = () => {
    if (currentStep === 3) {
      if (colorSubStep === 1 && colorPreferences.preferHues.length === 0) {
        setColorError("Please choose at least one color for your room")
        scrollToTop()
        return
      }

      if (colorSubStep < 3) {
        setColorSubStep(colorSubStep + 1)
        scrollToTop()
      } else {
        setCurrentStep(4)
        generateDesigns()
      }
    } else {
      setCurrentStep(currentStep + 1)
      if (currentStep === 2) {
        setColorSubStep(1)
      }
    }
  }

  const startWizardSession = async (aiSessionId: string, imageUrls: string[], passedUiSessionId: string) => {
    if (!session?.user?.id || !passedUiSessionId) {
      return null
    }

    try {
      const wizardPayload = {
        userId: session.user.id,
        userSessionId: passedUiSessionId,
        sessionId: aiSessionId,
        roomType: selections.roomType,
        roomTheme: selections.style,
        roomDimension: selections.roomSize,
        colorDetails: selections.colorDetails,
      }

      const result = await startScratchConceptWizard(wizardPayload)

      if (result.success && result.wizardId) {
        setWizardId(result.wizardId)
        await appendToWizardSession(result.wizardId, imageUrls)
        return result.wizardId
      } else {
        console.error("Failed to start wizard session:", result.error)
        setApiError("Unable to save wizard session, but your designs will still be preserved when creating a project.")
        return null
      }
    } catch (error) {
      console.error("Error starting wizard session:", error)
      setApiError("Unable to save wizard session, but your designs will still be preserved when creating a project.")
      return null
    }
  }

  const appendToWizardSession = async (wizardId: string, imageUrls: string[]) => {
    if (!wizardId || !imageUrls.length) return

    try {
      const result = await appendGenerationImages({
        wizardId,
        type: "scratch-concept",
        newImageUrls: imageUrls,
        increment: imageUrls.length,
      })

      if (!result.success) {
        console.warn("Failed to append images to wizard session:", result.error)
      }
    } catch (error) {
      console.error("Error appending images to wizard session:", error)
    }
  }

  const generateDesigns = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" })

    setIsGenerating(true)
    setApiError(null)

    const newUiSessionId = crypto.randomUUID()
    setUiSessionId(newUiSessionId)

    try {
      const result = await Promise.resolve(
        generateRoomConcepts({
          userId: session.user.id,
          roomType: selections.roomType,
          roomTheme: selections.style,
          roomDimension: selections.roomSize,
          colorDetails: selections.colorDetails,
          regeneration: false,
        }),
      ).catch((error) => {
        console.log("[v0] Caught promise rejection in generateDesigns:", error)
        throw error
      })

      if (result.success && result.designs) {
        setGeneratedDesigns(result.designs)
        setApiDesignImageUrls(result.designs)

        if (result.sessionid) {
          setSessionId(result.sessionid)
          await startWizardSession(result.sessionid, result.designs, newUiSessionId)
        }

        await refreshCredits()
      } else {
        setApiError(result.error || "Failed to generate designs")
        setGeneratedDesigns([])
        setApiDesignImageUrls([])
        setCurrentStep(3)
      }
    } catch (error) {
      console.log("[v0] Error caught in generateDesigns try-catch:", error)

      let errorMessage = error instanceof Error ? error.message : "Network error occurred while generating designs"
      if (errorMessage.startsWith("Network error occurred: ")) {
        errorMessage = errorMessage.replace("Network error occurred: ", "")
      }

      if (!isBillingError(errorMessage)) {
        console.error("Error generating designs from scratch:", error)
      }

      setApiError(errorMessage)
      setCurrentStep(3)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleRegenerate = async (newDesigns: string[]) => {
    console.log("[v0] StartFromScratchWizard handleRegenerate called")
    console.log("[v0] Scrolling to top from parent...")
    window.scrollTo({ top: 0, behavior: "instant" })

    setIsRegenerating(true)
    setApiError(null)

    try {
      const result = await Promise.resolve(
        generateRoomConcepts({
          userId: session?.user?.id,
          sessionId: sessionId,
          roomType: selections.roomType,
          roomTheme: selections.style,
          roomDimension: selections.roomSize,
          colorDetails: selections.colorDetails,
          regeneration: true,
        }),
      ).catch((error) => {
        console.log("[v0] Caught promise rejection in handleRegenerate:", error)
        throw error
      })

      if (result.success && result.designs) {
        if (!wizardId && result.sessionid) {
          console.log("[v0] Creating wizard session after successful regeneration")
          const newWizardId = await startWizardSession(
            result.sessionid,
            result.designs,
            uiSessionId || crypto.randomUUID(),
          )
          if (newWizardId) {
            setWizardId(newWizardId)
            setSessionId(result.sessionid)
          }
        } else if (wizardId) {
          await appendToWizardSession(wizardId, result.designs)
        }

        setGeneratedDesigns((prev) => [...prev, ...result.designs])
        setApiDesignImageUrls((prev) => [...prev, ...result.designs])
        await refreshCredits()
      } else {
        setApiError(result.error || "Failed to regenerate designs")
      }
    } catch (error) {
      console.log("[v0] Error caught in handleRegenerate try-catch:", error)

      let errorMessage = error instanceof Error ? error.message : "Network error occurred while regenerating designs"
      if (errorMessage.startsWith("Network error occurred: ")) {
        errorMessage = errorMessage.replace("Network error occurred: ", "")
      }

      if (!isBillingError(errorMessage)) {
        console.error("Error regenerating designs from scratch:", error)
      }

      setApiError(errorMessage)
    } finally {
      setIsRegenerating(false)
    }
  }

  const prevStep = () => {
    if (currentStep === 3 && colorSubStep > 1) {
      setColorSubStep(colorSubStep - 1)
    } else {
      setCurrentStep(currentStep - 1)
      if (currentStep === 4) {
        setColorSubStep(3)
      }
    }
  }

  const handleRoomTypeSelect = (roomType: string) => {
    updateSelection("roomType", roomType)
  }

  const totalRoomTypePages = Math.ceil(roomTypes.length / ROOM_TYPES_PER_PAGE)
  const currentRoomTypes = roomTypes.slice(roomTypePage * ROOM_TYPES_PER_PAGE, (roomTypePage + 1) * ROOM_TYPES_PER_PAGE)

  const nextRoomTypePage = () => {
    setRoomTypePage((prev) => (prev + 1) % totalRoomTypePages)
  }

  const prevRoomTypePage = () => {
    setRoomTypePage((prev) => (prev - 1 + totalRoomTypePages) % totalRoomTypePages)
  }

  const handleCreateProject = async () => {
    if (!selectedDesign || !session?.user?.id) {
      return
    }

    if (!wizardId || !sessionId) {
      setApiError("Unable to create project - wizard session not found. Please try regenerating designs.")
      return
    }

    setIsCreatingProject(true)
    setApiError(null)

    try {
      const roomTypeObj = roomTypes.find((room) => room.value === selections.roomType)
      const roomTypeDisplayName = roomTypeObj ? roomTypeObj.name : selections.roomType

      const projectName = roomTypeDisplayName

      const selectedDesignIndex = selectedDesign ? Number.parseInt(selectedDesign.split("-")[1] || "0") : 0
      const selectedDesignUrl = apiDesignImageUrls[selectedDesignIndex] || selectedDesign

      const projectPayload = {
        kind: "scratch" as const,
        data: {
          userId: session.user.id,
          wizardId: wizardId,
          name: projectName,
          sessionId: sessionId,
          roomType: selections.roomType,
          roomTheme: selections.style,
          roomDimension: selections.roomSize,
          colorDetails: selections.colorDetails,
          projectImageUrl: selectedDesignUrl,
        },
      }

      const result = await saveProject(projectPayload)

      if (result.success && result.projectId) {
        clearSavedState()
        console.log("[v0] Cleared saved state after successful project creation")
        window.location.href = `/dashboard/projects/${result.projectId}?kind=scratch`
      } else {
        setApiError(result.error || "Failed to create project")
        console.error("Failed to save project:", result.error)
      }
    } catch (error) {
      console.log("[v0] Error caught in handleCreateProject try-catch:", error)

      let errorMessage = error instanceof Error ? error.message : "Network error occurred while creating project"
      if (errorMessage.startsWith("Network error occurred: ")) {
        errorMessage = errorMessage.replace("Network error occurred: ", "")
      }

      if (!isBillingError(errorMessage)) {
        console.error("Error creating project:", error)
      }

      setApiError(errorMessage)
    } finally {
      setIsCreatingProject(false)
    }
  }

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return !!selections.roomType
      case 1:
        return !!selections.roomSize
      case 2:
        return !!selections.style
      case 3:
        return !!selections.colorDetails
      case 4:
        return !!selectedDesign
      default:
        return false
    }
  }

  const conceptGenerationCost = getActionCost("GENERATE_CONCEPTS_FROM_SCRATCH")

  return (
    <div className="space-y-6">
      {isRestoring ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#40A1AB]" />
            <p className="text-gray-600">Restoring your progress...</p>
          </div>
        </div>
      ) : (
        <>
          <WizardStepper steps={steps} currentStep={currentStep === 3 ? 4 : currentStep + 1} selections={selections} />

          {apiError &&
            (isBillingError(apiError) ? (
              <BillingErrorComponent errorMessage={apiError} onBeforePurchase={onBeforePurchase} />
            ) : (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <p className="text-red-800 text-sm">{apiError}</p>
                </CardContent>
              </Card>
            ))}

          {colorError && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <p className="text-red-800 text-sm">{colorError}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-6">
              {currentStep === 0 && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold">Select Room Type</h2>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        Page {roomTypePage + 1} of {totalRoomTypePages}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {currentRoomTypes.map((room) => {
                      const Icon = iconMap[room.icon as keyof typeof iconMap] || Sofa

                      return (
                        <div
                          key={room.value}
                          className={`flex flex-col items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                            selections.roomType === room.value
                              ? "border-[#40A1AB] bg-[#40A1AB]/10"
                              : "border-gray-200 hover:border-[#40A1AB]/50"
                          }`}
                          onClick={() => handleRoomTypeSelect(room.value)}
                        >
                          <div className="relative w-full h-32 mb-4 rounded-md overflow-hidden">
                            <Image
                              src={room.image || "/placeholder.svg"}
                              alt={room.name}
                              fill
                              className="object-cover"
                            />
                            {selections.roomType === room.value && (
                              <div className="absolute top-2 right-2 bg-[#40A1AB] text-white p-1 rounded-full">
                                <Check className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon className="h-5 w-5 text-[#40A1AB]" />
                            <span>{room.name}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className="flex justify-center items-center gap-4 mt-6">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={prevRoomTypePage}
                      className="h-9 w-9 rounded-full bg-transparent"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="flex gap-2">
                      {Array.from({ length: totalRoomTypePages }).map((_, index) => (
                        <Button
                          key={index}
                          variant={roomTypePage === index ? "default" : "outline"}
                          size="sm"
                          onClick={() => setRoomTypePage(index)}
                          className={`w-9 h-9 p-0 ${roomTypePage === index ? "bg-[#40A1AB] hover:bg-[#3A919A]" : ""}`}
                          aria-label={`Page ${index + 1}`}
                          aria-current={roomTypePage === index ? "page" : undefined}
                        >
                          {index + 1}
                        </Button>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={nextRoomTypePage}
                      className="h-9 w-9 rounded-full bg-transparent"
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <RoomSizeSelector
                  selectedSize={selections.roomSize}
                  onSelect={(size) => updateSelection("roomSize", size)}
                />
              )}

              {currentStep === 2 && (
                <StyleSelector selectedStyle={selections.style} onSelect={(style) => updateSelection("style", style)} />
              )}

              {currentStep === 3 && (
                <RoomColorSelections
                  currentStep={colorSubStep}
                  onStepChange={setColorSubStep}
                  onColorDetailsChange={handleColorDetailsChange}
                  initialMood={selectedMood}
                  initialIntensity={selectedIntensity}
                  initialAccentWall={selectedAccentWall}
                  initialPreferHues={colorPreferences.preferHues}
                  initialAvoidHues={colorPreferences.avoidHues}
                />
              )}

              {currentStep === 4 && (
                <>
                  {isGenerating ? (
                    <GenerationLoadingComponent
                      message="Bringing your designs to life..."
                      onComplete={() => {}}
                      estimatedDuration={Number(process.env.NEXT_PUBLIC_GENERATE_CONCEPTS_FROM_SCRATCH_PORTRAIT)}
                    />
                  ) : (
                    <GeneratedDesigns
                      designs={generatedDesigns}
                      selectedDesign={selectedDesign}
                      onSelect={setSelectedDesign}
                      isGenerating={isGenerating}
                      isRegenerating={isRegenerating}
                      onRegenerate={handleRegenerate}
                      onComplete={handleCreateProject}
                      onBack={() => setCurrentStep(3)}
                      isCreatingProject={isCreatingProject}
                      imageSet="scratch"
                      estimatedDuration={Number(process.env.NEXT_PUBLIC_GENERATE_CONCEPTS_FROM_SCRATCH_PORTRAIT)}
                    />
                  )}
                </>
              )}

              {currentStep < 4 && (
                <div className="flex justify-between mt-8">
                  {currentStep > 0 ? (
                    <Button variant="outline" onClick={prevStep}>
                      <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                  ) : (
                    <div></div>
                  )}

                  <Button
                    onClick={nextStep}
                    disabled={!canProceed()}
                    className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                  >
                    {currentStep === 3 && colorSubStep === 3 ? (
                      <>Generate Designs {conceptGenerationCost > 0 ? `(${conceptGenerationCost} credits)` : ""}</>
                    ) : (
                      <>
                        Next <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
